import { CoCoOrderAction, RelatedPartyRoles, StatusColor } from '../../common/enums.js';
import {
  cancelledMsg,
  changeOrderMsg,
  deliveredMsg,
  newOrderMsg,
  openMsg,
  partiallyDeliveredMsg,
  pendingApprovalMsg,
  processingMsg,
  scheduledMsg,
  t,
  terminateSuspendResumeOrderMsg,
  unknownMsg,
} from '../../common/i18n/index.js';
import { CustomerOrderStatus } from '../../generated/api/models.js';
import { TableUrlParams } from '../Table/tableConstants.js';
import type { CocoProductOrderDetail } from '../../generated/api/cocoProductOrderDetail.js';
import type { CocoProductOrderSummary } from '../../generated/api/cocoProductOrderSummary.js';

export const getOrderType = (order: CocoProductOrderSummary): string => {
  const rootItem = order.productOrderItem[0];

  // action type is of string only and is not defined consistent values yet, so these values may change in the future
  switch (rootItem.action) {
    case CoCoOrderAction.ADD:
      return t.PTQ7(newOrderMsg);
    case CoCoOrderAction.TERMINATE:
    case CoCoOrderAction.SUSPEND:
    case CoCoOrderAction.RESUME:
      return t.U3BJ(terminateSuspendResumeOrderMsg);
    case CoCoOrderAction.REPLACE_OFFER:
    case CoCoOrderAction.CHANGE_OWNERSHIP:
      return t.PZ9O(changeOrderMsg);
    default:
      return '';
  }
};

export const getOrderStateInfo = (orderStatus?: string): { color: StatusColor; text: string } => {
  switch (orderStatus) {
    case CustomerOrderStatus.COMPLETED:
      return { color: StatusColor.GREEN, text: t.PQGG(deliveredMsg) };
    case CustomerOrderStatus.FAILED:
    case CustomerOrderStatus.IN_PROGRESS:
    case CustomerOrderStatus.PARTIALLY_FAILED:
      return { color: StatusColor.YELLOW, text: t.VZZW(processingMsg) };

    case CustomerOrderStatus.PARTIALLY_COMPLETED:
      return { color: StatusColor.YELLOW, text: t.SJ7E(partiallyDeliveredMsg) };

    case 'OPEN':
    case CustomerOrderStatus.PENDING:
    case CustomerOrderStatus.NEW:
      return { color: StatusColor.GREY, text: t.TJ54(openMsg) };

    case CustomerOrderStatus.CANCELLED:
      return { color: StatusColor.GREY, text: t.PEKP(cancelledMsg) };

    case CustomerOrderStatus.SCHEDULED:
      return { color: StatusColor.GREY, text: t.PY03(scheduledMsg) };

    case CustomerOrderStatus.PENDING_APPROVAL:
      return { color: StatusColor.SALMON, text: t.ZXA6(pendingApprovalMsg) };

    case undefined:
    default:
      return { color: StatusColor.GREY, text: t.QWC8(unknownMsg) };
  }
};

export const getPartyName = (order: CocoProductOrderSummary | CocoProductOrderDetail, role: RelatedPartyRoles) =>
  order.relatedParty?.find(party => party.role === role)?.name ?? '';

export const getCompanyName = (order: CocoProductOrderSummary): string => {
  return (
    order.productOrderItem?.[0]?.relatedParty?.find(party => party.role === RelatedPartyRoles.CUSTOMER)?.name ?? ''
  );
};

export const getTopicText = (order: CocoProductOrderSummary | CocoProductOrderDetail): string => {
  return order.productOrderItem.map(item => item.action).join(', ');
};

export const isLegacyOrder = (): boolean => {
  const search = window.location.search;
  return new URLSearchParams(search).get(TableUrlParams.LEGACY) === 'true';
};
